import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import theme from "../../../../UI/theme";
import Dropdown from "../../../UiComponents/Dropdown";
import { getMusicPlayers, updateMusicPlayer } from "../../../../scripts/musicPlayer";
import { virtualDJModes, virtualDjDataSources } from "./utils";
import { getZones } from "../../../../scripts/zone";
import Checkbox from "../../../UiComponents/Checkbox";
import { isHubVersionAtLeast } from "../../../installation/modules/hub/hubUtils";
import PlaylistSettings from "../PlaylistSettings";
import MusicPlayer from "../../../musicPlayer/MusicPlayer";
import { TextMuted } from "../../../UiComponents/text/StyledText";
import ExpandCollapseButton from "../../../UiComponents/buttons/ExpandCollapseButton";
import InnerCard from "../../../UiComponents/containers/InnerCard";
import AnimateHeight from "react-animate-height";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    h2 {
        margin: 0;
    }
`;

const HeaderContainer = styled.div`
    padding: 10px;
`;

const OptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 10px;
    border-bottom: 2px solid ${theme.colors.darkSpace80};
    padding: 10px;

    h3 {
        margin-bottom: 0;
    }

    p {
        margin: 0;
        color: ${theme.colors.darkSpace30};
        font-size: ${theme.fontSize.small};
    }

    li {
        margin-bottom: 5px;
        color: ${theme.colors.darkSpace30};
        font-size: ${theme.fontSize.small};
    }
`;

const ZoneCheckboxContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 5px;
    width: 100%;
    padding: 0px 30px;
`;

const StokeText = styled.p`
    padding: 20px;
    color: ${theme.colors.darkSpace20};
    max-width: 450px;
`;

const NavigationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    :hover {
        cursor: pointer;
    }

    h4 {
        margin: 16px 0;
    }
`

const StyledCard = styled(InnerCard)`
    width: 100%;
    box-sizing: border-box;
    background-color: ${theme.colors.darkSpace90};
`


const VirtualDJSettings = ({ customerId, musicPlayerId, hubs }) => {
  const [player, setPlayer] = useState(null); // raw player object from DB
  const [playlistMusicPlayer, setPlaylistMusicPlayer] = useState(null); // MusicPlayer instance only for PlaylistSettings
  const [selectedVirtualDjMode, setSelectedVirtualDjMode] = useState(null);
  const [selectedVirtualDjDataSource, setSelectedVirtualDjDataSource] = useState(null);
  const [zones, setZones] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [missingCustomZonesErrorMessage, setMissingCustomZonesErrorMessage] = useState('');
  const [playlistsExpanded, setPlaylistsExpanded] = useState(false);
  const playlistMusicPlayerRef = useRef(null);

  const linkedHub = useMemo(() => {
    return hubs?.find(hub => hub.hubId === player?.hubId) ?? null;
  }, [hubs, player?.hubId]);

  const fetchPlayer = useCallback(async () => {
    const players = await getMusicPlayers(customerId);
    const foundPlayer = players?.find(p => p.musicPlayerId === musicPlayerId) ?? null;

    setPlayer(foundPlayer);

    setSelectedVirtualDjMode(
      foundPlayer ? (virtualDJModes.find(m => m.key === foundPlayer.virtualDjMode) ?? null) : null
    );
    setSelectedVirtualDjDataSource(
      foundPlayer ? (virtualDjDataSources.find(s => s.key === foundPlayer.virtualDjDataSource) ?? null) : null
    );
    setSelectedZones(foundPlayer?.virtualDjDataZones ?? []);

    playlistMusicPlayerRef.current?.cleanup();
    if (foundPlayer) {
      const mp = new MusicPlayer(foundPlayer, () => {});
      await mp.init();
      playlistMusicPlayerRef.current = mp;
      setPlaylistMusicPlayer(mp);
    } else {
      playlistMusicPlayerRef.current = null;
      setPlaylistMusicPlayer(null);
    }

    return foundPlayer;
  }, [customerId, musicPlayerId]);

  const fetchZones = useCallback(async () => {
    const z = await getZones(customerId);
    const visibleZones = Array.isArray(z) ? z.filter(zone => !zone.hide) : [];
    setZones(visibleZones);
  }, [customerId]);

  useEffect(() => {
    return () => {
      playlistMusicPlayerRef.current?.cleanup();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchPlayer(), fetchZones()]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fetchPlayer, fetchZones]);

  useEffect(() => {
    if (linkedHub) return;

    const intervalId = setInterval(async () => {
      await fetchPlayer();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [linkedHub, fetchPlayer]);

  const updateVirtualDJMode = async (virtualDJModeItem) => {
    setSelectedVirtualDjMode(virtualDJModeItem);
    setPlayer(prev => prev ? { ...prev, virtualDjMode: virtualDJModeItem?.key } : prev);

    await updateMusicPlayer({
      musicPlayerId: player?.musicPlayerId,
      virtualDjMode: virtualDJModeItem?.key
    });

    await fetchPlayer();
  };

  const updateVirtualDJDataSource = async (virtualDjDataSourceItem) => {
    setSelectedVirtualDjDataSource(virtualDjDataSourceItem);
    setPlayer(prev => prev ? { ...prev, virtualDjDataSource: virtualDjDataSourceItem?.key } : prev);

    const updatedMusicPlayer = {
      musicPlayerId: player?.musicPlayerId,
      virtualDjDataSource: virtualDjDataSourceItem?.key,
      hubId: player?.hubId
    };

    if (
      virtualDjDataSourceItem?.key === 'CUSTOM' &&
      (!player?.virtualDjDataZones || player.virtualDjDataZones.length === 0)
    ) {
      updatedMusicPlayer.virtualDjDataZones = zones.map(z => z.zoneId);
      setSelectedZones(updatedMusicPlayer.virtualDjDataZones);
    }

    await updateMusicPlayer(updatedMusicPlayer);
    await fetchPlayer();
  };

  const updateSelectedZones = async (zoneId) => {
    if (!zoneId) return;

    const newSelectedZones = selectedZones.includes(zoneId)
      ? selectedZones.filter(z => z !== zoneId)
      : [...selectedZones, zoneId];

    if (newSelectedZones.length === 0) {
      setMissingCustomZonesErrorMessage('At least one zone must be selected when using a custom data source.');
      return;
    }

    setMissingCustomZonesErrorMessage('');
    setSelectedZones(newSelectedZones);
    setPlayer(prev => prev ? { ...prev, virtualDjDataZones: newSelectedZones } : prev);

    await updateMusicPlayer({
      musicPlayerId: player?.musicPlayerId,
      virtualDjDataZones: newSelectedZones
    });

    await fetchPlayer();
  };

  return (
    <Container>
      <HeaderContainer>
        <h2>Waved Autopilot</h2>
        <TextMuted>Match your music to your crowd</TextMuted>
      </HeaderContainer>

      {(!loading && player?.musicPlayerId && linkedHub && isHubVersionAtLeast(linkedHub.programVersion, '10.2.0')) ? (
        <>
          <OptionContainer>
            <TextMuted>Mode</TextMuted>
            <Dropdown
              items={virtualDJModes}
              selectedItem={selectedVirtualDjMode}
              onClick={updateVirtualDJMode}
              placeholder="Select DJ Mode"
            />
            <p>{selectedVirtualDjMode?.description}</p>
          </OptionContainer>

          {player.virtualDjMode !== 'OFF' && (
            <>
              <OptionContainer>
                <TextMuted>Use Data From</TextMuted>
                <Dropdown
                  items={virtualDjDataSources}
                  selectedItem={selectedVirtualDjDataSource}
                  onClick={updateVirtualDJDataSource}
                  placeholder="Select Data Source"
                />
                <p>{selectedVirtualDjDataSource?.description}</p>

                {selectedVirtualDjDataSource?.key === 'CUSTOM' && zones.length > 0 && (
                  <>
                    {zones.map(zone => (
                      <ZoneCheckboxContainer key={zone.zoneId}>
                        <label>{zone.zoneName}</label>
                        <Checkbox
                          checked={selectedZones.includes(zone.zoneId)}
                          onChange={() => updateSelectedZones(zone.zoneId)}
                        />
                      </ZoneCheckboxContainer>
                    ))}
                    {missingCustomZonesErrorMessage && (
                      <p style={{ color: theme.colors.raspberryRed }}>
                        {missingCustomZonesErrorMessage}
                      </p>
                    )}
                  </>
                )}
              </OptionContainer>

              <OptionContainer style={{padding: '0'}}>
                <StyledCard>
                  <NavigationContainer onClick={() => setPlaylistsExpanded(prev => { return !prev })} >
                      <h4>Score your Playlists</h4>
                      <ExpandCollapseButton showState={playlistsExpanded} color={theme.colors.darkSpace60} />
                  </NavigationContainer>

                  <AnimateHeight duration={500} height={playlistsExpanded ? 'auto' : 0}>
                    <ol>
                      <li>Add playlist to Autopilot.</li>
                      <li>Score the energy of your playlists from 1 to 10.</li>
                    </ol>
                    <PlaylistSettings
                      musicPlayer={playlistMusicPlayer}
                      defaultPlaylistOrder="PLAYLIST_NAME" />
                  </AnimateHeight>
                </StyledCard>
               </OptionContainer>
              
            </>
          )}
        </>
      ) : (
        <>
          {loading ? (
            <OptionContainer>
              <p>Loading...</p>
            </OptionContainer>
          ) : (
            <>
              {(linkedHub && !isHubVersionAtLeast(linkedHub.programVersion, '10.2.0')) && (
                <StokeText>
                  Virtual DJ is available for clients where all hubs are running version 10.2.0 or later.
                  <br />
                  Contact Waved support for more information.
                </StokeText>
              )}

              {!linkedHub && (
                <StokeText>Contact Waved support for more information about the setup process</StokeText>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default VirtualDJSettings;